import {
  APPOINTMENT_CHECKOUT_STATUSES,
  AppointmentStatus,
  DepositCaptureStrategy,
  DepositForfeitureOutcomeStatus,
  DepositRequirementSource,
  DepositRequestStatus,
  DepositStatus,
  type IAppointment,
  type IDepositMetadata,
  type IDepositRequest,
} from '@principle-theorem/principle-core/interfaces';

/**
 * Answers the business questions callers actually have about a deposit's
 * status, so that no caller compares against a status value inline.
 *
 * This exists because `DepositRequestStatus.Cancelled` was doing four unrelated
 * jobs at once — "stop processing this", "this can be superseded", "do not send
 * the confirmation email", and "a release can resume here". Each caller meant
 * something different, so adding `Released` and `Refunded` to the enum would
 * have silently broken whichever ones were not updated, with no compiler help
 * because they are equality checks rather than exhaustive switches. Naming the
 * questions separately keeps the next status member a change to this file
 * instead of a hunt through the codebase.
 */
export class DepositLifecycle {
  /**
   * The request has reached an end state and no routine processing should
   * touch it again. Expiry sweeps, link supersession scans and background jobs
   * want this one.
   */
  static isClosedRequestStatus(status: DepositRequestStatus): boolean {
    return [
      DepositRequestStatus.Expired,
      DepositRequestStatus.Cancelled,
      DepositRequestStatus.Released,
      DepositRequestStatus.Refunded,
    ].includes(status);
  }

  /**
   * The patient can still pay this request. Deliberately narrower than "not
   * closed": an authorised hold is open but not payable, because the money is
   * already committed.
   */
  static isPatientPayableRequestStatus(status: DepositRequestStatus): boolean {
    return status === DepositRequestStatus.Pending;
  }

  /**
   * Real money is currently attached to this request, either held against the
   * patient's card or captured into the practice's account. Use for
   * money-at-risk logic and staff-attention flagging, never for "is it done".
   */
  static hasLiveDepositMoney(status: DepositRequestStatus): boolean {
    return [
      DepositRequestStatus.Authorised,
      DepositRequestStatus.Paid,
    ].includes(status);
  }

  /**
   * A fresh request may replace this one. Narrower than closed on purpose: a
   * released or refunded deposit represents a resolved outcome for an
   * appointment that happened, and re-raising against it would double-charge a
   * patient who has already been dealt with.
   */
  static isSupersedableRequestStatus(status: DepositRequestStatus): boolean {
    return [
      DepositRequestStatus.Expired,
      DepositRequestStatus.Cancelled,
    ].includes(status);
  }

  /**
   * The request is an uncaptured cancellation-insurance hold that is eligible
   * to be released right now. A forfeiture already in flight wins, because both
   * outcomes consume the same authorisation.
   */
  static isReleasableInsuranceHold(depositRequest: IDepositRequest): boolean {
    return (
      depositRequest.status === DepositRequestStatus.Authorised &&
      depositRequest.captureStrategy ===
        DepositCaptureStrategy.CaptureOnCancellationOrReschedule &&
      depositRequest.forfeitureOutcome?.status !==
        DepositForfeitureOutcomeStatus.Pending
    );
  }

  /**
   * The appointment has reached attendance and still carries a live hold, so a
   * staff member may release it by hand when the automatic release did not.
   * Attendance is the load-bearing half: before it, the hold is still
   * legitimately forfeitable and releasing would destroy cancellation
   * insurance.
   */
  static canStaffReleaseHold(appointment: IAppointment): boolean {
    const metadata = appointment.deposit?.metadata;
    if (!metadata) {
      return false;
    }
    return (
      DepositLifecycle.hasReachedAttendance(appointment.status) &&
      metadata.status === DepositStatus.Authorised &&
      metadata.captureStrategy ===
        DepositCaptureStrategy.CaptureOnCancellationOrReschedule
    );
  }

  /**
   * The patient has turned up. `CheckingOut` counts because that is the point
   * reception takes payment, which is where a stuck hold hurts the patient's
   * available balance.
   */
  static hasReachedAttendance(status: AppointmentStatus): boolean {
    return APPOINTMENT_CHECKOUT_STATUSES.includes(status);
  }

  /**
   * The appointment's deposit requirement was settled and must survive a
   * re-evaluation such as a reschedule, because there is no affordance to
   * re-record it.
   *
   * `Refunded` and `Forfeited` are deliberately absent. They were not locked
   * before this change either, and adding them would alter reschedule
   * behaviour beyond the scope of the release work.
   */
  static locksAppointmentDepositRequirement(status: DepositStatus): boolean {
    return [
      DepositStatus.Authorised,
      DepositStatus.Paid,
      DepositStatus.Waived,
      DepositStatus.Released,
    ].includes(status);
  }

  /**
   * Staff-added create-flow metadata can be removed only before a patient has
   * seen a request or money has moved; every caller must answer that question
   * the same way before offering an opt-out.
   */
  static canDiscardStaffAddedMetadata(metadata?: IDepositMetadata): boolean {
    return (
      metadata?.source === DepositRequirementSource.Staff &&
      [DepositStatus.Required, DepositStatus.Waived].includes(metadata.status)
    );
  }
}
